import { ref, watch } from "vue";

// ===== 型別定義 =====

/**
 * 條件篩選模式
 *
 * onHit_targetHas      — 傷害發生時，目標身上有指定 condition
 * onHit_attackerHas    — 傷害發生時，攻擊者身上有指定 condition（吃 debuff 的職業用）
 * duringCondition_target   — 指定 condition 在目標身上存在的期間內所有傷害
 * duringCondition_attacker — 指定 condition 在攻擊者身上存在的期間內所有傷害
 */
export type ConditionFilterMode =
    | "onHit_targetHas"
    | "onHit_attackerHas"
    | "duringCondition_target"
    | "duringCondition_attacker";

/** 單一條件規則（葉節點） */
export type ConditionFilterLeaf = {
    type: "leaf";
    mode: ConditionFilterMode;
    conditionId: number;
};

/** 組合規則（AND / OR，可巢狀） */
export type ConditionFilterGroup = {
    type: "and" | "or";
    rules: ConditionFilter[];
};

export type ConditionFilter = ConditionFilterLeaf | ConditionFilterGroup;

/**
 * 分組方式
 *
 * eachEntry — 每次進出條件算一個 group（例如崩壞每段分開統計）
 * none      — 全部合計，不分組
 */
export type GroupByMode = "eachEntry" | "none";

/** 自訂條件設定 */
export type CustomConditionConfig = {
    /** 唯一識別碼 */
    id: string;
    /** 顯示名稱，例如「崩壞期間爆發」 */
    displayName: string;
    /** 條件篩選規則 */
    filter: ConditionFilter;
    /** 結果分組方式 */
    groupBy: GroupByMode;
};

// ===== 預設自訂條件 =====

const DEFAULT_CUSTOM_CONDITION_CONFIGS: CustomConditionConfig[] = [
    {
        id: "cc_1776579683788_bd55k",
        displayName: "崩",
        filter: { type: "leaf", mode: "onHit_targetHas", conditionId: 803 },
        groupBy: "eachEntry",
    },
];

// ===== localStorage 存取 =====

const STORAGE_KEY = "summaryConditionConfigs";

function loadFromStorage(): CustomConditionConfig[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) return JSON.parse(raw) as CustomConditionConfig[];
    } catch {
        /* ignore */
    }
    return DEFAULT_CUSTOM_CONDITION_CONFIGS.map((c) => ({ ...c }));
}

function saveToStorage(configs: CustomConditionConfig[]) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(configs));
}

// ===== 響應式狀態 =====

export const customConditionConfigs = ref<CustomConditionConfig[]>(
    loadFromStorage(),
);

// ===== CRUD =====

/** 新增自訂條件（自動產生 id） */
export function addCustomCondition(
    config: Omit<CustomConditionConfig, "id">,
): CustomConditionConfig {
    const newConfig: CustomConditionConfig = {
        ...config,
        id: `cc_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    };
    customConditionConfigs.value = [...customConditionConfigs.value, newConfig];
    saveToStorage(customConditionConfigs.value);
    return newConfig;
}

/** 更新指定 id 的自訂條件 */
export function updateCustomCondition(
    id: string,
    updates: Partial<Omit<CustomConditionConfig, "id">>,
) {
    customConditionConfigs.value = customConditionConfigs.value.map((c) =>
        c.id === id ? { ...c, ...updates } : c,
    );
    saveToStorage(customConditionConfigs.value);
}

/** 刪除指定 id 的自訂條件 */
export function removeCustomCondition(id: string) {
    customConditionConfigs.value = customConditionConfigs.value.filter(
        (c) => c.id !== id,
    );
    saveToStorage(customConditionConfigs.value);
}

/** 調整順序（拖曳排序用） */
export function reorderCustomCondition(fromIndex: number, toIndex: number) {
    const list = [...customConditionConfigs.value];
    const [item] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, item);
    customConditionConfigs.value = list;
    saveToStorage(customConditionConfigs.value);
}

// ===== 狀態覆蓋率 Filter（summary2 與 playerComparison 共用） =====

const COND_FILTER_KEY = "mabidil_condFilters";

function loadCondFilters(): { bossIds: number[]; playerIds: number[] } {
    try {
        const raw = localStorage.getItem(COND_FILTER_KEY);
        if (raw) return JSON.parse(raw) as { bossIds: number[]; playerIds: number[] };
        // 首次遷移：從 bossUI2 讀取 bossCondIncludeIds
        const bossRaw = localStorage.getItem("mabidil_bossUI2");
        if (bossRaw) {
            const bossUI = JSON.parse(bossRaw);
            if (bossUI.bossCondIncludeIds?.length)
                return { bossIds: bossUI.bossCondIncludeIds as number[], playerIds: [] };
        }
    } catch { /* ignore */ }
    return { bossIds: [], playerIds: [] };
}

const _cf = loadCondFilters();

/** Boss 狀態覆蓋率 filter（summary2 與 playerComparison 共用） */
export const bossCondIncludeIds   = ref<number[]>(_cf.bossIds);
/** 玩家狀態覆蓋率 filter（summary2 與 playerComparison 共用） */
export const playerCondIncludeIds = ref<number[]>(_cf.playerIds);

/** 將兩個 filter 持久化至 localStorage */
export function saveCondFilters(): void {
    try {
        localStorage.setItem(COND_FILTER_KEY, JSON.stringify({
            bossIds:   bossCondIncludeIds.value,
            playerIds: playerCondIncludeIds.value,
        }));
    } catch { /* ignore */ }
}

// ===== 跨頁面共用選擇目標 =====

/** 目前選擇的目標 entity key（applyDamageBySkill 與 summary 共用） */
export const sharedSelectedTargetKey = ref<string>('');

// ===== 召喚物篩選設定 =====

const SUMMON_FILTER_KEY = "mabidil_summonFilter";

function loadSummonFilter(): boolean {
    try {
        const raw = localStorage.getItem(SUMMON_FILTER_KEY);
        if (raw !== null) return JSON.parse(raw) as boolean;
    } catch { /* ignore */ }
    return false;
}

/** 是否忽略人偶以外的召喚物傷害（summary 與 playerComparison 共用） */
export const ignoreSummonsExceptPuppet = ref<boolean>(loadSummonFilter());

watch(ignoreSummonsExceptPuppet, (v) => {
    try { localStorage.setItem(SUMMON_FILTER_KEY, JSON.stringify(v)); } catch { /* ignore */ }
});

// ===== 被動技能觸發規則 =====

/** 被動技能觸發偵測規則（戰鬥紀錄 / Summary / 玩家比較 共用） */
export type PassiveSkillRule = {
    id: string;
    skillId: number;
    ratioMin: number;   // % 例如 47（= 0.47）無套裝下界
    ratioMax: number;   // % 例如 49（= 0.49）無套裝上界
    ratioMin2?: number; // % 有套裝下界（可選）
    ratioMax2?: number; // % 有套裝上界（可選）
    lookback: number;   // 往前查幾秒
};

/** 被動技能觸發規則（固定，三頁共用；各±1% 容許整數截斷誤差，無套裝/有套裝各一段） */
export const passiveRules: readonly PassiveSkillRule[] = [
    { id: "p0", skillId: 58101, ratioMin: 47,   ratioMax: 49,   ratioMin2: 51,   ratioMax2: 53,   lookback: 5 }, // 閃焰（無套裝48%，有套裝52%）
    { id: "p1", skillId: 58100, ratioMin: 53,   ratioMax: 55,   ratioMin2: 57.5, ratioMax2: 59.5, lookback: 5 }, // 爆破（無套裝54%，有套裝58.5%）
    { id: "p2", skillId: 58009, ratioMin: 197,  ratioMax: 203,  ratioMin2: 227,  ratioMax2: 233,  lookback: 5 }, // 連續攻擊（無套裝200%，有套裝230%）
];

// ===== 人品設定 =====

/** 單一星塵槽位設定 */
export type StardustConfig = {
    id: string;
    label: string;    // 顯示名稱，例如「星塵 1」
    skillId: number;  // 觸發技能 ID，0 = 未設定
    rate: number;     // 觸發機率 % e.g. 30
    cooldownSec: number; // 冷卻秒數
};

/** 人品判斷設定 */
export type LuckConfig = {
    critRateBase: number;         // 基礎暴擊率 % e.g. 56.5
    critRateBarrierBonus: number; // 結界加成 % e.g. 1.0
    chainRateBase: number;        // 連續攻擊基礎觸發率 % e.g. 5.0
    chainRateSetBonus: number;    // 破壞套加成 % e.g. 1.0
    stardustConfigs: StardustConfig[];
};

const LUCK_CONFIG_KEY = "mabidil_luckConfig";

const DEFAULT_LUCK_CONFIG: LuckConfig = {
    critRateBase: 56.5,
    critRateBarrierBonus: 1.0,
    chainRateBase: 5.0,
    chainRateSetBonus: 1.0,
    stardustConfigs: [
        { id: "sd0", label: "閃焰",   skillId: 58101, rate: 30, cooldownSec: 2 },
        { id: "sd1", label: "爆破",   skillId: 58100, rate: 30, cooldownSec: 2 },
        { id: "sd2", label: "籠罩反彈", skillId: 58104, rate: 30, cooldownSec: 2 },
    ],
};

function loadLuckConfig(): LuckConfig {
    try {
        const raw = localStorage.getItem(LUCK_CONFIG_KEY);
        if (raw) {
            const parsed = JSON.parse(raw) as Partial<LuckConfig>;
            // 遷移：舊設定若 skillId=0 自動補上預設 skillId（首次設定此欄位）
            const stardusts = DEFAULT_LUCK_CONFIG.stardustConfigs.map((def, i) => {
                const saved = (parsed.stardustConfigs ?? [])[i];
                if (!saved) return { ...def };
                return { ...saved, skillId: saved.skillId !== 0 ? saved.skillId : def.skillId };
            });
            return { ...DEFAULT_LUCK_CONFIG, ...parsed, stardustConfigs: stardusts };
        }
    } catch { /* ignore */ }
    return { ...DEFAULT_LUCK_CONFIG, stardustConfigs: DEFAULT_LUCK_CONFIG.stardustConfigs.map((c) => ({ ...c })) };
}

export const luckConfig = ref<LuckConfig>(loadLuckConfig());

watch(luckConfig, (v) => {
    try { localStorage.setItem(LUCK_CONFIG_KEY, JSON.stringify(v)); } catch { /* ignore */ }
}, { deep: true });

// ===== 匯出 / 匯入（分享用） =====

/** 將所有設定匯出為 JSON 字串 */
export function exportConfigs(): string {
    return JSON.stringify(customConditionConfigs.value, null, 2);
}

/** 從 JSON 字串匯入設定（覆蓋現有） */
export function importConfigs(json: string): boolean {
    try {
        const parsed = JSON.parse(json) as CustomConditionConfig[];
        if (!Array.isArray(parsed)) return false;
        customConditionConfigs.value = parsed;
        saveToStorage(parsed);
        return true;
    } catch {
        return false;
    }
}

// ===== 建立輔助函式 =====

/** 建立一個空的 leaf filter */
export function makeLeafFilter(
    mode: ConditionFilterMode = "onHit_targetHas",
    conditionId = 0,
): ConditionFilterLeaf {
    return { type: "leaf", mode, conditionId };
}

/** 建立一個 AND 組合 filter */
export function makeAndFilter(
    rules: ConditionFilter[] = [],
): ConditionFilterGroup {
    return { type: "and", rules };
}

/** 建立一個 OR 組合 filter */
export function makeOrFilter(
    rules: ConditionFilter[] = [],
): ConditionFilterGroup {
    return { type: "or", rules };
}

/** 建立一個預設空白的自訂條件（尚未加入 storage） */
export function makeEmptyConditionConfig(): Omit<CustomConditionConfig, "id"> {
    return {
        displayName: "",
        filter: makeLeafFilter(),
        groupBy: "eachEntry",
    };
}

// ===== 顯示輔助 =====

const MODE_LABEL: Record<ConditionFilterMode, string> = {
    onHit_targetHas: "傷害時目標有",
    onHit_attackerHas: "傷害時攻擊者有",
    duringCondition_target: "目標 condition 存在期間",
    duringCondition_attacker: "攻擊者 condition 存在期間",
};

export function getModeLabelTw(mode: ConditionFilterMode): string {
    return MODE_LABEL[mode];
}

/** 將 filter 轉為人類可讀的描述字串（用於 UI 預覽） */
export function describeFilter(
    filter: ConditionFilter,
    condNameMap: Record<number, string>,
): string {
    if (filter.type === "leaf") {
        const condName =
            condNameMap[filter.conditionId] ?? `#${filter.conditionId}`;
        return `${MODE_LABEL[filter.mode]}「${condName}」`;
    }

    const sep = filter.type === "and" ? " 且 " : " 或 ";
    const parts = filter.rules.map((r) => describeFilter(r, condNameMap));
    return parts.length === 1 ? parts[0] : `(${parts.join(sep)})`;
}
